<template>
  <Teleport to="body">
    <Transition name="cancel-confirm">
      <div
        v-if="show"
        ref="dialogRoot"
        class="cancel-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancelConfirmTitle"
        tabindex="-1"
        @keydown.esc="close"
      >
        <button
          type="button"
          class="cancel-confirm__backdrop"
          aria-label="確認画面を閉じる"
          @click="close"
        ></button>

        <section class="cancel-confirm__panel">
          <header class="cancel-confirm__header">
            <div class="cancel-confirm__icon" aria-hidden="true">!</div>
            <div>
              <p class="cancel-confirm__eyebrow">最終確認</p>
              <h2 id="cancelConfirmTitle" class="cancel-confirm__title">
                年間予定作業を中止してもよろしいですか？
              </h2>
              <p class="cancel-confirm__lead">
                {{ actionText }}と同時に、チェックした年間予定作業を中止として登録します。
              </p>
            </div>
          </header>

          <div class="cancel-confirm__summary">
            <div class="cancel-confirm__summary-item">
              <span class="cancel-confirm__summary-label">中止する作業</span>
              <strong>{{ cancellations.length }}件</strong>
            </div>
            <div class="cancel-confirm__summary-divider"></div>
            <div class="cancel-confirm__summary-item">
              <span class="cancel-confirm__summary-label">請求から除外する金額</span>
              <strong>{{ formatCurrency(excludedAmount) }}</strong>
            </div>
          </div>

          <div class="cancel-confirm__body">
            <p class="cancel-confirm__section-title">中止対象</p>
            <div class="cancel-confirm__list">
              <article
                v-for="item in cancellations"
                :key="item.client_row_key"
                class="cancel-confirm__item"
              >
                <div class="cancel-confirm__item-main">
                  <div class="cancel-confirm__item-meta">
                    <span class="cancel-confirm__date">
                      {{ item.target_work_year }}年{{ item.target_work_month }}月
                    </span>
                    <span v-if="item.building_nm" class="cancel-confirm__building">
                      {{ item.building_nm }}
                    </span>
                  </div>
                  <p class="cancel-confirm__work">{{ item.work_content }}</p>
                </div>
                <strong v-if="item.fixed_fee_billing" class="cancel-confirm__amount cancel-confirm__amount--unchanged">
                  定額・金額変更なし
                </strong>
                <strong v-else class="cancel-confirm__amount">{{ formatCurrency(item.bill_amount) }}</strong>
              </article>
            </div>

            <div class="cancel-confirm__notice">
              <span class="cancel-confirm__notice-mark" aria-hidden="true">i</span>
              <p>
                中止履歴には「{{ reasonText }}」と記録されます。処理後に戻す場合は、年間予定作業画面から中止取消を行ってください。
              </p>
            </div>
            <div v-if="fixedFeeCancellationCount > 0" class="cancel-confirm__fixed-fee-notice">
              定額請求の作業 {{ fixedFeeCancellationCount }}件は請求項目から除外されますが、定額の請求金額は変わりません。
            </div>
          </div>

          <footer class="cancel-confirm__footer">
            <button type="button" class="btn btn-light cancel-confirm__cancel" :disabled="isSubmitting" @click="close">
              内容を見直す
            </button>
            <button type="button" class="btn btn-danger cancel-confirm__submit" :disabled="isSubmitting" @click="$emit('confirm')">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
              {{ isSubmitting ? '処理中...' : confirmButtonText }}
            </button>
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';

const props = defineProps({
  show: Boolean,
  cancellations: {
    type: Array,
    default: () => []
  },
  mode: {
    type: String,
    default: 'create'
  },
  isSubmitting: Boolean
});

const emit = defineEmits(['close', 'confirm']);
const dialogRoot = ref(null);

const isUpdate = computed(() => props.mode === 'update');
const actionText = computed(() => isUpdate.value ? '請求書の更新' : '請求書の登録');
const reasonText = computed(() => isUpdate.value
  ? '請求書更新時に作業未実施を確認'
  : '請求書作成時に作業未実施を確認');
const confirmButtonText = computed(() => isUpdate.value
  ? '中止して請求書を更新'
  : '中止して請求書を登録');
const excludedAmount = computed(() => props.cancellations.reduce(
  (sum, item) => item.fixed_fee_billing ? sum : sum + (Number(item.bill_amount) || 0),
  0
));
const fixedFeeCancellationCount = computed(() => props.cancellations.filter(
  (item) => item.fixed_fee_billing
).length);

watch(() => props.show, async (visible) => {
  if (!visible) return;
  await nextTick();
  dialogRoot.value?.focus();
});

const close = () => {
  if (!props.isSubmitting) emit('close');
};

const formatCurrency = (value) => new Intl.NumberFormat('ja-JP', {
  style: 'currency',
  currency: 'JPY',
  maximumFractionDigits: 0
}).format(Number(value) || 0);
</script>

<style scoped>
.cancel-confirm {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: 24px;
}

.cancel-confirm__backdrop {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  background: rgba(15, 23, 42, 0.66);
  backdrop-filter: blur(3px);
}

.cancel-confirm__panel {
  position: relative;
  width: min(720px, 100%);
  max-height: min(820px, calc(100vh - 48px));
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.34);
}

.cancel-confirm__header {
  display: grid;
  grid-template-columns: 52px 1fr;
  gap: 18px;
  padding: 28px 30px 22px;
  background: linear-gradient(135deg, #fff7ed 0%, #fff 68%);
  border-bottom: 1px solid #fed7aa;
}

.cancel-confirm__icon {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: #dc2626;
  color: #fff;
  font-size: 30px;
  font-weight: 800;
  box-shadow: 0 8px 18px rgba(220, 38, 38, 0.24);
}

.cancel-confirm__eyebrow {
  margin: 0 0 4px;
  color: #b45309;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.14em;
}

.cancel-confirm__title {
  margin: 0;
  color: #0f172a;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.35;
}

.cancel-confirm__lead {
  margin: 8px 0 0;
  color: #64748b;
  line-height: 1.65;
}

.cancel-confirm__summary {
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  gap: 24px;
  margin: 22px 30px 0;
  padding: 18px 22px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.cancel-confirm__summary-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.cancel-confirm__summary-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.cancel-confirm__summary-item strong {
  color: #0f172a;
  font-size: 20px;
}

.cancel-confirm__summary-divider {
  background: #cbd5e1;
}

.cancel-confirm__body {
  max-height: 420px;
  overflow-y: auto;
  padding: 22px 30px 26px;
}

.cancel-confirm__section-title {
  margin: 0 0 10px;
  color: #334155;
  font-size: 13px;
  font-weight: 800;
}

.cancel-confirm__list {
  display: grid;
  gap: 9px;
}

.cancel-confirm__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #ef4444;
  border-radius: 10px;
  background: #fff;
}

.cancel-confirm__item-main {
  min-width: 0;
}

.cancel-confirm__item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 5px;
}

.cancel-confirm__date,
.cancel-confirm__building {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.cancel-confirm__date {
  background: #fee2e2;
  color: #b91c1c;
}

.cancel-confirm__building {
  overflow: hidden;
  max-width: 320px;
  background: #e2e8f0;
  color: #334155;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cancel-confirm__work {
  margin: 0;
  overflow-wrap: anywhere;
  color: #1e293b;
  font-weight: 700;
}

.cancel-confirm__amount {
  flex: 0 0 auto;
  color: #991b1b;
  font-size: 16px;
  white-space: nowrap;
}

.cancel-confirm__amount--unchanged {
  color: #0a58ca;
  font-size: 12px;
}

.cancel-confirm__fixed-fee-notice {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid #9ec5fe;
  border-radius: 10px;
  background: #e7f1ff;
  color: #084298;
  font-size: 13px;
  font-weight: 600;
}

.cancel-confirm__notice {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  padding: 13px 15px;
  border-radius: 10px;
  background: #eff6ff;
  color: #1e3a8a;
  font-size: 13px;
  line-height: 1.6;
}

.cancel-confirm__notice p {
  margin: 0;
}

.cancel-confirm__notice-mark {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border-radius: 50%;
  background: #2563eb;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}

.cancel-confirm__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 18px 30px 24px;
  border-top: 1px solid #e2e8f0;
  background: #fff;
}

.cancel-confirm__cancel,
.cancel-confirm__submit {
  min-height: 44px;
  padding-inline: 22px;
  border-radius: 10px;
  font-weight: 700;
}

.cancel-confirm__cancel {
  border: 1px solid #cbd5e1;
  color: #334155;
}

.cancel-confirm__submit {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 8px 18px rgba(220, 38, 38, 0.2);
}

.cancel-confirm-enter-active,
.cancel-confirm-leave-active {
  transition: opacity 0.18s ease;
}

.cancel-confirm-enter-active .cancel-confirm__panel,
.cancel-confirm-leave-active .cancel-confirm__panel {
  transition: transform 0.18s ease, opacity 0.18s ease;
}

.cancel-confirm-enter-from,
.cancel-confirm-leave-to {
  opacity: 0;
}

.cancel-confirm-enter-from .cancel-confirm__panel,
.cancel-confirm-leave-to .cancel-confirm__panel {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

@media (max-width: 576px) {
  .cancel-confirm {
    padding: 12px;
  }

  .cancel-confirm__panel {
    max-height: calc(100vh - 24px);
    border-radius: 16px;
  }

  .cancel-confirm__header {
    grid-template-columns: 42px 1fr;
    gap: 12px;
    padding: 20px;
  }

  .cancel-confirm__icon {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    font-size: 24px;
  }

  .cancel-confirm__title {
    font-size: 19px;
  }

  .cancel-confirm__summary {
    gap: 12px;
    margin: 16px 20px 0;
    padding: 14px;
  }

  .cancel-confirm__body {
    padding: 18px 20px;
  }

  .cancel-confirm__item {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .cancel-confirm__footer {
    padding: 16px 20px 20px;
  }
}
</style>
